import React, { useState, useEffect } from 'react';
import { 
  Star, Play, Calendar, Users, Crown, Sparkles, 
  Award, Trophy, Zap, Camera, Film, Volume2,
  ChevronLeft, ChevronRight, Eye, Heart, Share2
} from 'lucide-react';

const CelebrityAppearances = () => {
  const [currentVideo, setCurrentVideo] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Celebrity appearances data - you can expand this array with more appearances
  const appearances = [
    {
      id: 1,
      title: "2025 Season Welcome Remarks",
      celebrity: "Ryan Fitzpatrick",
      type: "Season Welcome",
      videoUrl: "/fitzfinal.mp4",
      thumbnail: "/fitz_thumbnail.jpg", // You'll need to add this image
      date: "January 2025",
      description: "The legendary journeyman quarterback delivers an inspiring welcome message to kick off the 2025 Beasts of Bellevue fantasy football season.",
      stats: {
        views: "1,247",
        likes: "89",
        duration: "2:34"
      },
      tags: ["Welcome", "Season Opener", "NFL Legend"],
      backgroundColor: "from-purple-900 via-blue-800 to-indigo-900",
      accentColor: "purple"
    }
    // Add more celebrity appearances here as you get them
  ];

  const currentAppearance = appearances[currentVideo];

  const nextVideo = () => {
    setCurrentVideo((prev) => (prev + 1) % appearances.length);
  };

  const prevVideo = () => {
    setCurrentVideo((prev) => (prev - 1 + appearances.length) % appearances.length);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-purple-500/20 blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-yellow-400/60 rounded-full animate-bounce"></div>
        <div className="absolute top-3/4 right-1/3 w-3 h-3 bg-purple-400/60 rounded-full animate-bounce delay-700"></div>
        <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-blue-400/60 rounded-full animate-bounce delay-1400"></div>
      </div>

      {/* Header Section */}
      <div className="relative z-10 pt-8 pb-6">
        <div className="max-w-7xl mx-auto px-6">
          {/* Premium Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-32 h-32 mb-6 relative">
              <img 
                src="/beastmic.png" 
                alt="Beast Microphone" 
                className="h-32 w-32 object-contain drop-shadow-2xl"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden w-32 h-32 bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 rounded-full items-center justify-center">
                <Camera className="h-16 w-16 text-white drop-shadow-lg" />
              </div>
              <div className="absolute inset-0 bg-orange-500/30 rounded-full blur-xl animate-pulse"></div>
            </div>
            
            <h1 className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 mb-4 tracking-tight">
              Celebrity Appearances
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
NFL Legends and celebrities stop by League HQ to ensure shame and glory are meted out with appropriate gravitas.            </p>
          </div>
        </div>
      </div>

      {/* Section Banner */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 mb-8">
        <div className="bg-gradient-to-r from-orange-600/80 via-red-600/80 to-purple-600/80 backdrop-blur-xl rounded-2xl p-6 border border-orange-500/30 shadow-2xl">
          <div className="text-center">
            <h2 className="text-4xl font-black text-white mb-2 tracking-wide drop-shadow-lg">
              2025 Season Welcoming Remarks
            </h2>
            <p className="text-orange-100 text-lg font-medium">
              An exclusive message to kick off our championship season
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Video Player Section */}
          <div className="lg:col-span-2">
            <div className="relative group">
              {/* Premium Video Container */}
              <div className={`relative rounded-3xl overflow-hidden bg-gradient-to-br ${currentAppearance.backgroundColor} p-1 shadow-2xl`}>
                <div className="relative rounded-3xl overflow-hidden bg-black">
                  {/* Video Element */}
                  <video
                    className="w-full aspect-video object-cover"
                    controls
                    poster="/beasthead.png"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  >
                    <source src={currentAppearance.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  
                  {/* Video Overlay UI */}
                  <div className="absolute top-4 left-4 pointer-events-none">
                    <div className="bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2">
                      <div className="flex items-center space-x-2">
                        <div className="relative">
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                          <div className="absolute inset-0 w-2 h-2 bg-red-400 rounded-full animate-ping opacity-75"></div>
                          <div className="absolute inset-0 w-2 h-2 bg-red-300 rounded-full animate-pulse delay-200 opacity-50"></div>
                        </div>
                        <span className="text-white text-sm font-medium tracking-wide">BEAST TV</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Controls */}
              {appearances.length > 1 && (
                <div className="flex justify-between items-center mt-6">
                  <button
                    onClick={prevVideo}
                    className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 text-white transition-all duration-300 hover:scale-105 active:scale-95"
                  >
                    <ChevronLeft className="h-5 w-5" />
                    <span>Previous</span>
                  </button>
                  
                  <div className="flex space-x-2">
                    {appearances.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentVideo(index)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${
                          index === currentVideo
                            ? 'bg-yellow-400 scale-125'
                            : 'bg-white/30 hover:bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                  
                  <button
                    onClick={nextVideo}
                    className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full px-6 py-3 text-white transition-all duration-300 hover:scale-105 active:scale-95"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Celebrity Info & Details */}
          <div className="space-y-6">
            {/* Celebrity Celebration Card */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="text-center">
                {/* Celebrity Image */}
                <div className="relative mx-auto w-40 h-40 mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-pulse"></div>
                  <div className="relative w-full h-full bg-gray-300 rounded-full flex items-center justify-center text-5xl font-bold text-gray-600 overflow-hidden">
                    {/* Replace this with actual celebrity image */}
                    <img 
                      src="/fitz_headshot.jpg" 
                      alt={currentAppearance.celebrity}
                      className="w-full h-full object-cover object-center"
                      style={{ objectPosition: 'center 30%' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-full bg-gradient-to-br from-purple-500 to-blue-500 rounded-full items-center justify-center">
                      <Star className="h-16 w-16 text-white" />
                    </div>
                  </div>
                </div>
                
                {/* Celebrity Name - Large and Prominent */}
                <h2 className="text-5xl font-black text-white mb-4 tracking-wide drop-shadow-xl">
                  {currentAppearance.celebrity}
                </h2>
                
                {/* NFL Journeyman QB Badge */}
                <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-yellow-400/30 to-orange-500/30 rounded-full border border-yellow-400/50 mb-6">
                  <Crown className="h-6 w-6 text-yellow-400 mr-3" />
                  <span className="text-yellow-200 text-lg font-bold tracking-wide">NFL JOURNEYMAN QB</span>
                </div>

                {/* Celebration Message */}
                <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-2xl p-6 border border-orange-400/30">
                  <p className="text-white text-xl font-medium leading-relaxed">
                    "That's not consistency, that's commitment to mediocrity!"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coming Soon Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mb-6 mx-auto">
              <Zap className="h-8 w-8 text-white" />
            </div>
            
            <h3 className="text-3xl font-bold text-white mb-4">More Appearances Coming Soon</h3>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-6">
              Celebrities have been swinging by the League to offer their well-wishes and excoriations for many years. Those will be posted here soon.
            </p>
            
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-full border border-purple-400/30">
              <Sparkles className="h-5 w-5 text-purple-400 mr-2" />
              <span className="text-purple-200 font-medium">Beasts of Bellevue Media</span>
            </div>
          </div>
        </div>
      </div>

      {/* Additional styling for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CelebrityAppearances;